import { authTokens, setAuthTokens } from "@/data/store"
import { AuthTokens } from "@/model/AuthTokens"
import { CustomerSupportOverviewDto } from "@/model/CustomerSupport.ts"
import { DeviceKit } from "@/model/DeviceOrders"
import { Device, DeviceVendor } from "@/model/Devices"
import { Jwt } from "@/model/Jwt"
import { LoginCredentials } from "@/model/LoginCredentials"
import {
  isMfaChallengeResponse,
  LoginResponse,
  LoginResult,
  MfaChallenge,
  MfaChallengeApiDto,
  toMfaChallenge,
} from "@/model/LoginResponse"
import { Organization } from "@/model/Organization.ts"
import {
  AddPatientDto,
  ImportedPatientApiDto,
  PatientApiDto,
  PatientDetailPageDataApiDto,
  PrescriptionReportApiDto,
} from "@/model/Patient"
import {
  CreatePregnancyApiDto,
  SupportalPregnancyApiDto,
  UpdatePregnancyApiDto,
} from "@/model/Pregnancy"
import {
  CreateDiabetesPrescriptionDto,
  CreateHypertensionPrescriptionDto,
  DiabetesPrescriptionApiDto,
  HypertensionPrescriptionApiDto,
  OffboardReason,
  UpdateDiabetesPrescriptionDto,
  UpdateHypertensionPrescriptionDto,
} from "@/model/Prescription.ts"
import { ReminderDayTimeApiDto } from "@/model/Reminders.ts"
import { SupportUserApiDto, toSupportUser } from "@/model/SupportUser"
import { Instant } from "@js-joda/core"
import { merge } from "lodash"

const backendUrl = `${import.meta.env.VITE_BACKEND_API_URL as string}`
const appVersion = `${import.meta.env.VITE_APP_VERSION as string}`

export class HttpError extends Error {
  statusCode: number
  responseJson?: Record<string, unknown>

  constructor(
    statusCode: number,
    url: string,
    responseJson?: Record<string, unknown>
  ) {
    super(`HttpError: ${statusCode} ${url} ${JSON.stringify(responseJson)}`)
    this.statusCode = statusCode
    this.responseJson = responseJson
  }
}

export const jsonFetch = async <T>(url: string, options: RequestInit = {}) => {
  const defaultOptions: RequestInit = {
    headers: {
      "Content-Type": "application/json",
      "X-App-Version": appVersion,
    },
  }

  const response = await fetch(url, merge(defaultOptions, options))
  const responseBody = await response.text()
  const responseJson = responseBody ? JSON.parse(responseBody) : undefined

  if (!response.ok) {
    throw new HttpError(response.status, url, responseJson)
  }

  return responseJson as T
}

const storeAuthTokens = ({ accessJwt, refreshJwt }: LoginResponse) => {
  const authTokens: AuthTokens = { accessJwt, refreshJwt }
  setAuthTokens(authTokens)
  return authTokens
}

let refreshingTokensPromise: Promise<AuthTokens> | null = null

const refreshTokens = async (refreshToken: string) => {
  const actuallyRefreshTokens = async () => {
    try {
      const response = await jsonFetch<LoginResponse>(
        `${backendUrl}/v1/token/refresh`,
        {
          method: "POST",
          body: JSON.stringify({ refreshToken }),
        }
      )
      return storeAuthTokens(response)
    } catch (error) {
      // BioT can say LOGIN_FAILED even if the token hasn't yet expired based on time, probably some other invalidation logic on their end.
      if (error instanceof HttpError && error.statusCode === 403) {
        setAuthTokens(null) // This will trigger a redirect to the login page
      }
      throw error
    }
  }

  // This ensures that only one refresh token operation is performed at a time.
  if (!refreshingTokensPromise) {
    refreshingTokensPromise = actuallyRefreshTokens().finally(() => {
      refreshingTokensPromise = null
    })
  }

  return refreshingTokensPromise
}

export const authenticatedFetch = async <T>(
  url: string,
  options: RequestInit = {}
) => {
  const { accessJwt, refreshJwt } = authTokens()!
  const accessJwtExpiration = Instant.parse(accessJwt.expiration)
  const isAboutToExpire = Instant.now().isAfter(
    accessJwtExpiration.minusSeconds(150)
  ) // User access tokens should stay valid for 15 minutes, we refresh them 2m30s min advance to be sure that it doesn't expire during a long running request.
  const refreshJwtString = refreshJwt.token
  let accessToken = accessJwt
  if (isAboutToExpire) {
    const refreshedTokens: AuthTokens = await refreshTokens(refreshJwtString)
    accessToken = refreshedTokens.accessJwt
  }
  try {
    return await _authenticatedFetch<T>({
      url,
      accessJwt: accessToken,
      options,
    })
  } catch (error) {
    if (error instanceof HttpError && error.statusCode === 401) {
      const refreshedTokens: AuthTokens = await refreshTokens(refreshJwtString)
      return await _authenticatedFetch<T>({
        url,
        accessJwt: refreshedTokens.accessJwt,
        options,
      })
    }
    throw error
  }
}

const _authenticatedFetch = async <T>({
  url,
  accessJwt,
  options = {},
}: {
  url: string
  accessJwt: Jwt
  options?: RequestInit
}) => {
  const defaultOptions = {
    headers: {
      Authorization: `Bearer ${accessJwt.token}`,
    },
  }
  return await jsonFetch<T>(url, merge(defaultOptions, options))
}

const logInAndStoreTokens = async (
  url: string,
  body: object
): Promise<LoginResult> => {
  const response = await jsonFetch<LoginResponse | MfaChallengeApiDto>(url, {
    method: "POST",
    body: JSON.stringify(body),
  })
  if (isMfaChallengeResponse(response)) {
    return { type: "mfa-challenge", challenge: toMfaChallenge(response) }
  }
  return { type: "authenticated", authTokens: storeAuthTokens(response) }
}

export const logInWithEmailAndPassword = ({
  email,
  password,
}: LoginCredentials) =>
  logInAndStoreTokens(`${backendUrl}/v1/login`, { email, password })

export type LogInWithMfaCodeParams = {
  challenge: MfaChallenge
  code: string
}

export const logInWithMfaCode = async ({
  challenge,
  code,
}: LogInWithMfaCodeParams) => {
  const response = await jsonFetch<LoginResponse>(
    `${backendUrl}/v1/login/mfa`,
    {
      method: "POST",
      body: JSON.stringify({
        challenge: challenge.method,
        userId: challenge.userId,
        session: challenge.session,
        code,
      }),
    }
  )
  return storeAuthTokens(response)
}

export const logOut = () => {
  jsonFetch(`${backendUrl}/v1/logout`, {
    method: "POST",
    body: JSON.stringify({
      refreshToken: authTokens()!.refreshJwt.token,
    }),
  }).catch(() => {}) // Run the logout request to the backend, but don't wait for it or handle the error
  setAuthTokens(null)
}

export type ResendInvitationEmailParams = {
  email: string
}

export const resendInvitationEmail = ({ email }: ResendInvitationEmailParams) =>
  jsonFetch(`${backendUrl}/v1/invitation/resend`, {
    method: "POST",
    body: JSON.stringify({ email }),
  })

export type CheckSignUpInvitationParams = {
  token: string
  userId: string
}

export const checkSignUpInvitation = async ({
  token,
  userId,
}: CheckSignUpInvitationParams) => {
  await jsonFetch(`${backendUrl}/v1/invitation/check`, {
    method: "POST",
    body: JSON.stringify({ token, userId }),
  })
}

export type SignUpParams = {
  token: string
  userId: string
  password: string
}

export const signUp = ({ token, userId, password }: SignUpParams) =>
  logInAndStoreTokens(`${backendUrl}/v1/invitation/accept`, {
    token,
    userId,
    password,
  })

export const getSelfUser = async () => {
  const dto = await authenticatedFetch<SupportUserApiDto>(`${backendUrl}/self`)
  return toSupportUser(dto)
}

export type SendForgotPasswordEmailParams = {
  email: string
}

export const sendForgotPasswordEmail = ({
  email,
}: SendForgotPasswordEmailParams) =>
  jsonFetch(`${backendUrl}/v1/password/forgot`, {
    method: "POST",
    body: JSON.stringify({ email }),
  })

export type CheckResetPasswordTokenParams = {
  token: string
  userId: string
}

export const checkResetPasswordToken = async ({
  token,
  userId,
}: CheckResetPasswordTokenParams) => {
  await jsonFetch(`${backendUrl}/v1/password/reset/check`, {
    method: "POST",
    body: JSON.stringify({ token, userId }),
  })
}

export type ResetPasswordParams = {
  token: string
  userId: string
  password: string
}

export const resetPassword = ({
  token,
  userId,
  password,
}: ResetPasswordParams) =>
  logInAndStoreTokens(`${backendUrl}/v1/password/reset`, {
    token,
    userId,
    password,
  })

type GetPatientDetailPageDataParams = {
  bloomlifeId: string
}

export const getPatientDetailPageData = ({
  bloomlifeId,
}: GetPatientDetailPageDataParams) =>
  authenticatedFetch<PatientDetailPageDataApiDto>(
    `${backendUrl}/patients/${bloomlifeId}`
  )

type PatientsOverviewPageDataApiDto = {
  organizations: Organization[]
  patients: PatientApiDto[]
  prescriptions: (HypertensionPrescriptionApiDto | DiabetesPrescriptionApiDto)[]
}

export const getPatientsOverviewPageData = async () =>
  await authenticatedFetch<PatientsOverviewPageDataApiDto>(
    `${backendUrl}/patients`
  )

export const getOrganizations = async () =>
  await authenticatedFetch<Organization[]>(`${backendUrl}/organizations`)

export type GetPatientByMRNParams = {
  organizationId: string
  medicalRecordNumber: string
}

export const getPatientByMRN = ({
  organizationId,
  medicalRecordNumber,
}: GetPatientByMRNParams) =>
  authenticatedFetch<PatientApiDto | undefined>(
    `${backendUrl}/organizations/${organizationId}/patients/mrn/${medicalRecordNumber}`
  )

export const getEMRPatientByMRN = ({
  organizationId,
  medicalRecordNumber,
}: GetPatientByMRNParams) =>
  authenticatedFetch<ImportedPatientApiDto | undefined>(
    `${backendUrl}/organizations/${organizationId}/emr/patients/mrn/${medicalRecordNumber}`
  )

export const checkBloomlifeId = async (bloomlifeId: string) => {
  try {
    await authenticatedFetch(
      `${backendUrl}/patients/bloomlifeId/${bloomlifeId}/check`,
      {
        method: "GET",
      }
    )
    return { isInUse: false }
  } catch (error) {
    if (error instanceof HttpError && error.statusCode === 409) {
      return { isInUse: true }
    }
    throw error
  }
}

export type AddPatientParams = {
  organizationId: string
  patient: AddPatientDto
}

export const addPatient = ({ organizationId, patient }: AddPatientParams) =>
  authenticatedFetch<PatientApiDto>(
    `${backendUrl}/organizations/${organizationId}/patients`,
    {
      method: "POST",
      body: JSON.stringify(patient),
    }
  )

type UpdateRemindersParams = {
  bloomlifeId: string
  reminderType: "measurement" | "medication"
  reminders: ReminderDayTimeApiDto[]
}

export const updateReminders = ({
  bloomlifeId,
  reminderType,
  reminders,
}: UpdateRemindersParams) =>
  authenticatedFetch<{
    measurement: ReminderDayTimeApiDto[]
    medication: ReminderDayTimeApiDto[]
  }>(`${backendUrl}/patients/${bloomlifeId}/reminders/${reminderType}`, {
    method: "PUT",
    body: JSON.stringify(reminders),
  })

export type UpdatePatientDto = {
  firstName: string
  lastName: string
  emailAddress?: string
  phoneNumber?: string
  dateOfBirth?: string
  preferredLocale?: string
  timeZone?: string
  insuranceProvider?: string
  shippingAddress?: {
    address1?: string
    address2?: string
    city?: string
    state?: string
    zipCode?: string
  }
  isAttCompatible?: boolean
}

type EditPatientInformationParams = {
  bloomlifeId: string
  update: UpdatePatientDto
}

export const editPatientInformation = ({
  bloomlifeId,
  update,
}: EditPatientInformationParams) =>
  authenticatedFetch<PatientApiDto>(`${backendUrl}/patients/${bloomlifeId}`, {
    method: "PATCH",
    body: JSON.stringify(update),
  })

type AddDeviceParams = {
  bloomlifeId: string
  imei: string
}

type AddDeviceByKitParams = {
  bloomlifeId: string
  vendor: DeviceVendor
  kitId: string
}

type UnlinkDeviceParams = {
  vendor: string
  serialNumber: string
}

type SuspendDeviceParams = {
  vendor: string
  serialNumber: string
  reason: string
}

type UnsuspendDeviceParams = {
  vendor: string
  serialNumber: string
}

export const addDevice = ({ bloomlifeId, imei }: AddDeviceParams) =>
  authenticatedFetch<Device>(`${backendUrl}/patients/${bloomlifeId}/device`, {
    method: "POST",
    body: JSON.stringify({ imei }),
  })

export const addDeviceByKit = ({
  bloomlifeId,
  vendor,
  kitId,
}: AddDeviceByKitParams) =>
  authenticatedFetch<Device>(
    `${backendUrl}/patients/${bloomlifeId}/device/kit`,
    {
      method: "POST",
      body: JSON.stringify({ vendor, kitId }),
    }
  )

export const unlinkDevice = ({ vendor, serialNumber }: UnlinkDeviceParams) =>
  authenticatedFetch<Device>(`${backendUrl}/devices/unlink`, {
    method: "POST",
    body: JSON.stringify({
      vendor,
      serialNumber,
    }),
  })

export const suspendDevice = ({
  vendor,
  serialNumber,
  reason,
}: SuspendDeviceParams) =>
  authenticatedFetch<Device>(`${backendUrl}/devices/suspend`, {
    method: "POST",
    body: JSON.stringify({
      vendor,
      serialNumber,
      reason,
    }),
  })

export const unsuspendDevice = ({
  vendor,
  serialNumber,
}: UnsuspendDeviceParams) =>
  authenticatedFetch<Device>(`${backendUrl}/devices/unsuspend`, {
    method: "POST",
    body: JSON.stringify({
      vendor,
      serialNumber,
    }),
  })

type AddPrescriptionParams = {
  bloomlifeId: string
  prescription:
    | CreateDiabetesPrescriptionDto
    | CreateHypertensionPrescriptionDto
}

export const addPrescription = ({
  bloomlifeId,
  prescription,
}: AddPrescriptionParams) =>
  authenticatedFetch<
    HypertensionPrescriptionApiDto | DiabetesPrescriptionApiDto
  >(`${backendUrl}/patients/${bloomlifeId}/prescriptions`, {
    method: "POST",
    body: JSON.stringify(prescription),
  })

type UpdatePrescriptionParams = {
  prescriptionId: string
  update: Partial<
    UpdateHypertensionPrescriptionDto | UpdateDiabetesPrescriptionDto
  >
}

export const updatePrescription = ({
  prescriptionId,
  update,
}: UpdatePrescriptionParams) =>
  authenticatedFetch<
    HypertensionPrescriptionApiDto | DiabetesPrescriptionApiDto
  >(`${backendUrl}/prescriptions/${prescriptionId}`, {
    method: "PATCH",
    body: JSON.stringify(update),
  })

export type AddPregnancyParams = {
  bloomlifeId: string
  create: CreatePregnancyApiDto
}

export const addPregnancy = ({ bloomlifeId, create }: AddPregnancyParams) =>
  authenticatedFetch<SupportalPregnancyApiDto>(
    `${backendUrl}/patients/${bloomlifeId}/pregnancies`,
    {
      method: "POST",
      body: JSON.stringify(create),
    }
  )

export type UpdatePregnancyParams = {
  bloomlifeId: string
  pregnancyId: string
  update: UpdatePregnancyApiDto
}

export const updatePregnancy = ({
  bloomlifeId,
  pregnancyId,
  update,
}: UpdatePregnancyParams) =>
  authenticatedFetch<SupportalPregnancyApiDto>(
    `${backendUrl}/patients/${bloomlifeId}/pregnancies/${pregnancyId}`,
    {
      method: "PATCH",
      body: JSON.stringify(update),
    }
  )

type ClosePrescriptionParams = {
  prescriptionId: string
  endDate: string
}

export const closePrescription = ({
  prescriptionId,
  endDate,
}: ClosePrescriptionParams) =>
  updatePrescription({
    prescriptionId,
    update: { endDate },
  })

export type OffboardPrescriptionParams = {
  prescriptionId: string
  reason: OffboardReason
  note?: string
}

export const offboardPrescription = ({
  prescriptionId,
  reason,
  note,
}: OffboardPrescriptionParams) =>
  authenticatedFetch<void>(
    `${backendUrl}/prescriptions/${prescriptionId}/offboard`,
    {
      method: "POST",
      body: JSON.stringify({ reason, note }),
    }
  )

export const reopenPrescription = (prescriptionId: string) =>
  authenticatedFetch<
    HypertensionPrescriptionApiDto | DiabetesPrescriptionApiDto
  >(`${backendUrl}/prescriptions/${prescriptionId}/reopen`, {
    method: "POST",
  })

export const sendPatientInvitation = (bloomlifeId: string) =>
  authenticatedFetch<void>(`${backendUrl}/patients/${bloomlifeId}/invite`, {
    method: "POST",
  })

export const sendResetPasswordEmail = (email: string) =>
  authenticatedFetch<void>(`${backendUrl}/v1/password/forgot`, {
    method: "POST",
    body: JSON.stringify({ email }),
  })

export const getCustomerSupportOverview = async () =>
  authenticatedFetch<CustomerSupportOverviewDto | undefined>(
    `${backendUrl}/customer-support/overview`
  )

export type AddCSFlagParams = {
  bloomlifeId: string
  reason: string
}

export const addCSFlag = async ({ bloomlifeId, reason }: AddCSFlagParams) => {
  return authenticatedFetch<void>(
    `${backendUrl}/patients/${bloomlifeId}/customer-support-flag-reason`,
    {
      method: "PATCH",
      body: JSON.stringify({ reason }),
    }
  )
}

export type RemoveCSFlagParams = {
  bloomlifeId: string
}

export const removeCSFlag = async ({ bloomlifeId }: RemoveCSFlagParams) => {
  return authenticatedFetch<void>(
    `${backendUrl}/patients/${bloomlifeId}/customer-support-flag-reason`,
    {
      method: "DELETE",
    }
  )
}

export type OrderDeviceParams = {
  bloomlifeId: string
  prescriptionId: string
  vendor: DeviceVendor
  deviceKitId: string
}

export const orderDevice = async ({
  bloomlifeId,
  prescriptionId,
  vendor,
  deviceKitId,
}: OrderDeviceParams) =>
  authenticatedFetch<{ notes: string }>(
    `${backendUrl}/patients/${bloomlifeId}/device/orders`,
    {
      method: "POST",
      body: JSON.stringify({
        prescriptionId,
        vendor,
        deviceKitId,
      }),
    }
  )

export const getDeviceOrderKits = async () =>
  authenticatedFetch<DeviceKit[]>(`${backendUrl}/devices/orders/kits`)

type UpdateCustomerSupportNotesParams = {
  bloomlifeId: string
  notes: string
}

export const updateCustomerSupportNotes = ({
  bloomlifeId,
  notes,
}: UpdateCustomerSupportNotesParams) =>
  authenticatedFetch<{ notes: string }>(
    `${backendUrl}/patients/${bloomlifeId}/customer-support-notes`,
    {
      method: "PATCH",
      body: JSON.stringify({ notes }),
    }
  )

type GetPrescriptionReportParams = {
  prescriptionId: string
}

export const getPrescriptionReport = ({
  prescriptionId,
}: GetPrescriptionReportParams) =>
  authenticatedFetch<PrescriptionReportApiDto>(
    `${backendUrl}/prescriptions/${prescriptionId}/report`
  )
